import { Stack, StackProps, Typography, TypographyProps, useMediaQuery, useTheme } from "@mui/material";
import AppCardAlert from "components/v2/organisms/app-card-alert/AppCardAlert";
import useAppCardAlert from "components/v2/organisms/app-card-alert/AppCardAlert.hooks";
import useAppDialog from "components/v2/organisms/app-dialog/AppDialog.hook";
import DemoBottomBanner from "components/v2/organisms/demo-bottom-banner/DemoBottomBanner";
import DemoLoginDialog, { DemoLoginDialogProps } from "components/v2/organisms/demo-login-dialog/DemoLoginDialog";
import ActionItemsCard, {
    ActionItemsCardProps,
} from "components/v2/templates/instant-vat/dashboard-new/ActionItemsCard";
import InstantVatSavingsCard, {
    InstantVatSavingsCardProps,
} from "components/v2/templates/instant-vat/dashboard-new/InstantVatSavingsCard";
import EmptyCard from "components/v2/templates/instant-vat/dashboard-new/vat-return-card/EmptyCard";
import VatReturnsAndRefunds, {
    VatReturnsAndRefund,
    VatReturnsAndRefundsProps,
} from "components/v2/templates/instant-vat/dashboard/VatReturnsAndRefunds";
import BenefitsDialog, { BenefitsDialogProps } from "components/v2/templates/instant-vat/demo/BenefitsDialog";
import { usePartnerId } from "hooks/partner-id";
import router from "next/router";
import { PageProps } from "pages/_app";
import { useEffect } from "react";

// Function to get the first day of last month
const getFirstDayOfLastMonth = (): string => {
    const now = new Date();
    const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    firstDayOfLastMonth.setDate(2);
    return firstDayOfLastMonth.toISOString().split("T")[0];
};

// Function to get the last day of last month
const getLastDayOfLastMonth = (): string => {
    const now = new Date();
    const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    return lastDayOfLastMonth.toISOString().split("T")[0];
};

// Function to get the second day of last month
const getSecondDayOfLastMonth = (): string => {
    const now = new Date();
    const secondDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    secondDayOfLastMonth.setDate(3);
    return secondDayOfLastMonth.toISOString().split("T")[0];
};

// Function to get the fifth day of last month
const getFifthDayOfLastMonth = (): string => {
    const now = new Date();
    const fifthDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    fifthDayOfLastMonth.setDate(6);
    return fifthDayOfLastMonth.toISOString().split("T")[0];
};

// Function to get the 10th day of last month
const getTenthDayOfLastMonth = (): string => {
    const now = new Date();
    const tenthDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    tenthDayOfLastMonth.setDate(11);
    return tenthDayOfLastMonth.toISOString().split("T")[0];
};

// Mock VAT return and refund list that matches the query structure for demo/testing
const mockVatReturnAndRefundList = {
    data: {
        data: [
            {
                status: "adsum funded" as const,
                position: "repayment" as const,
                vat_return_period_key: "24AH",
                vat_return_period_start: getFirstDayOfLastMonth(),
                vat_return_period_end: getLastDayOfLastMonth(),
                vat_return_submission_date: getSecondDayOfLastMonth(),
                vat_return_due_date: "2024-10-07",
                funded_amount: 47730.97,
                outstanding_amount: 0.0,
                hmrc_paid_out_date: getTenthDayOfLastMonth(),
                hmrc_approved_date: getFifthDayOfLastMonth(),
                adsum_fees_rate: 1.0,
                adsum_fees: 978.09,
                is_eligible_for_funding: true,
                offset: 0.0,
                liability_zero_at: getFifthDayOfLastMonth(),
                funded_date: getFifthDayOfLastMonth(),
                dilutions_check: null,
                monthly_fixed_fee: null,
                vat_amount: 50000.0,
                compliance_check: false,
                paid_at_adsum_bank: true,
            },
        ] as unknown as VatReturnsAndRefund[], //Just for demo purposes as the data is not real
        total_count: 1,
    },
    isPending: false,
    isLoading: false,
};

function Dashboard(props: PageProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"), {
        defaultMatches: true,
    });
    const { partner } = usePartnerId();

    const loginDialog = useAppDialog();
    const benefitsDialog = useAppDialog();

    const vatReturnAndRefundList = mockVatReturnAndRefundList;

    const mockEligibilityCheck = {
        data: {
            check: "check_active_instantvat_access",
        },
    };

    const appCardAlert = useAppCardAlert({
        showCloseButton: true,
    });

    const methods = {
        getActionItems() {
            const items: any[] = [];
            const eligibilityInfo = mockEligibilityCheck.data;

            if (eligibilityInfo) {
                const { check } = eligibilityInfo;

                if (check === "check_active_instantvat_access") {
                    items.push({
                        variant: "success",
                        heading: "Welcome to InstantVAT!",
                        text: (
                            <>
                                You&apos;re currently in the demo account so please feel free to look around. When
                                you&apos;re ready, create and{" "}
                                <span
                                    style={{
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        color: "inherit",
                                    }}
                                    onClick={() => loginDialog.open()}>
                                    activate your account
                                </span>
                                .
                            </>
                        ),
                        linkProps: {
                            children: "Activate account",
                            onClick: () => loginDialog.open(),
                        },
                    });
                }
            }

            return items;
        },
    };

    const stackProps: StackProps = {
        sx: {
            gap: isMobile ? 3 : 5,
            padding: isMobile ? 2 : 5,
            paddingTop: isMobile ? 2 : 8,
            paddingBottom: isMobile ? "140px" : "80px",
        },
    };

    const topStackProps: StackProps = {
        sx: {
            gap: 3,
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: isMobile ? "flex-start" : "space-between",
        },
    };

    const companyNameTextProps: TypographyProps = {
        variant: "h1",
        sx: {
            display: isMobile ? "none" : "block",
            color: "black",
        },
        children: `Your InstantVAT account`,
    };

    const contentStackProps: StackProps = {
        sx: {
            gap: 3,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            flexWrap: "wrap",
            alignItems: "flex-start",
        },
    };

    const vatReturnsAndRefundsProps: VatReturnsAndRefundsProps = {
        pageProps: props,
        items: vatReturnAndRefundList.data?.data || [],
        onClick() {
            let url = "/v2/instant-vat/vat-return";

            if (partner) {
                url += `?partner_id=${partner}`;
            }

            router.push(url);
        },
        demo: true,
    };

    const instantVatSavingsCardProps: InstantVatSavingsCardProps = {
        totalRefunds: 10,
        daysSaved: 147,
        totalReceived: 123456.67,
        dialog: loginDialog,
        demo: true,
    };

    const demoLoginDialogProps: DemoLoginDialogProps = {
        dialog: loginDialog,
        partnerId: partner,
    };

    const benefitsDialogProps: BenefitsDialogProps = {
        dialog: benefitsDialog,
        onActivateAccount: () => {
            benefitsDialog.close();
            loginDialog.open();
        },
    };

    const actionItemsCardProps: ActionItemsCardProps = {
        items: methods.getActionItems(),
    };

    useEffect(() => {
        // Show login dialog on page load
        loginDialog.open();
    }, []);

    const getVatCard = () => {
        return (
            <Stack
                sx={{
                    width: "100%",
                    minWidth: isMobile ? "100%" : "460px",
                    maxWidth: isMobile ? "100%" : "460px",
                    order: {
                        xs: 1,
                        sm: 1,
                    },
                }}>
                {vatReturnAndRefundList.data?.data?.length ? (
                    <VatReturnsAndRefunds {...vatReturnsAndRefundsProps} />
                ) : (
                    <EmptyCard />
                )}
            </Stack>
        );
    };

    const getSavingsCard = () => {
        return (
            <Stack
                sx={{
                    width: "100%",
                    flex: 1,
                    order: 3,
                    minWidth: isMobile ? "100%" : "400px",
                    maxWidth: isMobile ? "100%" : "400px",
                }}>
                <InstantVatSavingsCard {...instantVatSavingsCardProps} />
            </Stack>
        );
    };

    const getActionItemsCard = () => {
        return (
            <Stack
                sx={{
                    width: "100%",
                    maxWidth: isMobile ? "100%" : "420px",
                    minWidth: isMobile ? "100%" : "420px",
                    order: {
                        xs: 2,
                        sm: 2,
                    },
                }}>
                <ActionItemsCard {...actionItemsCardProps} />
            </Stack>
        );
    };

    return (
        <>
            <Stack {...stackProps}>
                <Stack {...topStackProps}>
                    <Typography {...companyNameTextProps} />
                </Stack>
                <Stack {...contentStackProps}>
                    {getVatCard()}
                    {getActionItemsCard()}
                    {getSavingsCard()}
                </Stack>
                {appCardAlert.state.open && <AppCardAlert {...appCardAlert.appCardAlertProps} />}
            </Stack>
            <DemoBottomBanner dialog={loginDialog} benefitsDialog={benefitsDialog} />
            <DemoLoginDialog {...demoLoginDialogProps} />
            <BenefitsDialog {...benefitsDialogProps} />
        </>
    );
}

export default Dashboard;
